import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { AXNUIMainContainer } from "@iddataweb/idw-common-ui/containers";
import { StaticRedirectUrlTypes } from "@iddataweb/idw-common-ui/enums";
import { getMatchesStaticRedirectUrl } from "@iddataweb/idw-common-ui/utils";
import {
    getAPIKey,
    getAsi,
    getGeneralTheme,
    getTmxInDCUI,
    getTMXSessionId
} from "@redux/selectors/verification-configuration-selectors";
import { VerificationForm } from "../verification-form/verification-form";
import { Route, Routes } from "react-router-dom";
import { ErrorPage } from "../error-page/error-page";
import { SuccessPage } from "../success-page/success-page";
import { WaitPage } from "../wait-page/wait-page";
import { MobileCompletePage } from "../mobile-complete-page/mobile-complete-page";
import { StaticLandingPage } from "../static-landing-page/static-landing-page";
import { CustomLandingPage } from "../custom-landing-page/custom-landing-page";

declare global {
    interface Window {
        idwPageHasLoaded: boolean;
    }
}

type MainContainerProps = {
    useThemedUI: boolean;
}

export const MainContainer = (props: MainContainerProps) => {
    const { useThemedUI } = props;
    const generalTheme = useSelector(getGeneralTheme);
    const tmxSessionId = useSelector(getTMXSessionId);
    const tmxInDCUI = useSelector(getTmxInDCUI);
    const asi = useSelector(getAsi);
    const apikey = useSelector(getAPIKey);

    useEffect(() => {
        window.idwPageHasLoaded = false;

        const onPageLoad = () => {
            if (!tmxSessionId || (tmxSessionId && !tmxInDCUI)) {
                window.idwPageHasLoaded = true;
            }
        };

        // Check if the page has already loaded
        if (document.readyState === 'complete') {
            return onPageLoad();
        } else {
            window.addEventListener('load', onPageLoad);
            // Remove the event listener when component unmounts
            return () => window.removeEventListener('load', onPageLoad);
        }
    }, []);

    const isStaticLandingPage = useMemo(() => {
        return getMatchesStaticRedirectUrl(window.location.pathname);
    }, [window.location.pathname]);

    return (
        <AXNUIMainContainer
            generalTheme={generalTheme}
            isStaticLandingPage={isStaticLandingPage}
            useThemedUI={useThemedUI}
            asi={asi}
            apikey={apikey}
        >
            <Routes>
                <Route path={"/error"} element={<ErrorPage useThemedUI={useThemedUI} />} />
                <Route path={"/axnui/error"} element={<ErrorPage useThemedUI={useThemedUI} />} />
                <Route path={"/success"} element={<SuccessPage useThemedUI={useThemedUI} />} />
                <Route path={"/axnui/success"} element={<SuccessPage useThemedUI={useThemedUI} />} />
                <Route path={"/"} element={<VerificationForm useThemedUI={useThemedUI} />} />
                <Route path={"/axnui"} element={<VerificationForm useThemedUI={useThemedUI} />} />
                { /* ASYNC WAITING PAGE */}
                <Route path={"/wait"} element={<WaitPage />} />
                <Route path={"/axnui/wait"} element={<WaitPage />} />
                {/* MOBILE COMPLETE PAGE */}
                <Route path={"/mobile_complete"} element={<MobileCompletePage />} />
                <Route path={"/axnui/mobile_complete"} element={<MobileCompletePage />} />
                {/* STATIC MOCKRP ENDPOINTS */}
                <Route path={StaticRedirectUrlTypes.VIEW_ASSERTIONS} element={<StaticLandingPage />} />
                <Route path={`/axnui/${StaticRedirectUrlTypes.VIEW_ASSERTIONS}`} element={<StaticLandingPage />} />
                <Route path={StaticRedirectUrlTypes.VIEW_ATTRIBUTES} element={<StaticLandingPage />} />
                <Route path={`/axnui/${StaticRedirectUrlTypes.VIEW_ATTRIBUTES}`} element={<StaticLandingPage />} />
                <Route path={StaticRedirectUrlTypes.VIEW_ENDPOINT} element={<StaticLandingPage />} />
                <Route path={`/axnui/${StaticRedirectUrlTypes.VIEW_ENDPOINT}`} element={<StaticLandingPage />} />
                <Route path={StaticRedirectUrlTypes.VIEW_ID_TOKEN} element={<StaticLandingPage />} />
                <Route path={`/axnui/${StaticRedirectUrlTypes.VIEW_ID_TOKEN}`} element={<StaticLandingPage />} />
                <Route path={StaticRedirectUrlTypes.VIEW_POLICY_DECISION} element={<StaticLandingPage />} />
                <Route path={`/axnui/${StaticRedirectUrlTypes.VIEW_POLICY_DECISION}`} element={<StaticLandingPage />} />
                {/* CUSTOM MOCKRP ENDPOINTS */}
                <Route path={"/display/*"} element={<CustomLandingPage />} />
                <Route path={"/axnui/display/*"} element={<CustomLandingPage />} />
            </Routes>
        </AXNUIMainContainer>
    );
};